import React, { useEffect, useState } from 'react';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import { NO_BREAK_SPACE } from '../../../../../constants';
import { ButtonDemo } from '../../../../../constants/settings-events';
import { useAdaptedSettings, DynamicElementProvider } from '../../../../../hooks';
import { getPlanValidityCycle } from '../../../../../utils';
import { orderPlansForMobileView } from '../../../../../utils/orderPlansForMobileView';
import { Plan } from '../Plan';
import { classes } from './PlanList.st.css';

// Max item width + margins;
export const MAX_ITEM_WIDTH = 454 + 60;
export const DYNAMIC_HEIGHT_ELEMENTS = [
  { name: 'planName' },
  { name: 'tagline', minHeight: 40 },
  { name: 'setup-fee', minHeight: 40 },
];

export interface PlanListProps {
  plans: PublicPlan[];
  showDemoHighlight?: boolean;
  buttonDemo: ButtonDemo;
  expandMobileBenefits: boolean;
  selectPlan(plan: PublicPlan): void;
  selectedPlanId?: string;
}

export const PlanList: React.FC<PlanListProps> = ({
  plans,
  showDemoHighlight,
  buttonDemo,
  selectPlan,
  expandMobileBenefits,
  selectedPlanId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const settings = useAdaptedSettings();
  const { t } = useTranslation();
  const { isMobile, isEditorX } = useEnvironment();

  const highlightedPlanId = settings.getHighlightedPlanId(plans, showDemoHighlight);
  const orderedPlans = isMobile && highlightedPlanId ? orderPlansForMobileView(plans, highlightedPlanId) : plans;

  const recurringPlansExist = orderedPlans.some((p) => p.pricing?.subscription);
  const freeTrialDaysExist = orderedPlans.some((p) => p.pricing?.freeTrialDays);
  const taglinesExist = orderedPlans.some((p) => p.description);
  const setupFeesExist = orderedPlans.some((p) => p.pricing?.feeConfigs?.length);
  const validityCyclesExist = orderedPlans.some((p) => getPlanValidityCycle(p, t) !== NO_BREAK_SPACE);
  const anyBenefits = orderedPlans.some((p) => p.perks?.values?.length);

  const maxWidth = MAX_ITEM_WIDTH * orderedPlans.length;

  const onSelectPlan = (plan: PublicPlan) => {
    setIsLoading(true);
    selectPlan(plan);
  };

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (isLoading) {
      /* 
        Reset loading state automatically after 5 seconds
        in case checkout is on the same page as PlanList widget
        and component isn't destroyed on navigation
      */
      timeout = setTimeout(() => {
        setIsLoading(false);
      }, 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isLoading]);

  return (
    <DynamicElementProvider elements={DYNAMIC_HEIGHT_ELEMENTS}>
      <ul className={classes.container} style={isEditorX ? {} : { maxWidth }}>
        {orderedPlans.map((plan) => (
          <li key={plan.id} data-hook={VIEWER_HOOKS.PLAN_LIST_ITEM}>
            <Plan
              buttonDemo={buttonDemo}
              expandMobileBenefits={expandMobileBenefits}
              anyBenefits={anyBenefits}
              highlighted={highlightedPlanId === plan.id}
              plan={plan}
              recurringPlansExist={recurringPlansExist}
              freeTrialDaysExist={freeTrialDaysExist}
              validityCyclesExist={validityCyclesExist}
              taglinesExist={taglinesExist}
              setupFeesExist={setupFeesExist}
              onClick={() => onSelectPlan(plan)}
              loading={isLoading && plan.id === selectedPlanId}
            />
          </li>
        ))}
      </ul>
    </DynamicElementProvider>
  );
};
